/**
 * API client for in-app notifications (issue #1436, EPIC #608).
 *
 * Talks to the self-scoped ``/api/v1/notifications/`` endpoints: the server only
 * ever returns / mutates the CALLER's own notifications, so the frontend never
 * needs (and cannot) reach another user's notifications.
 */
import { apiRequest } from "./client";

export type NotificationType =
  | "general"
  | "task_assigned"
  | "task_due"
  | "task_overdue"
  | "task_overdue_digest"
  | "deal_stage_changed"
  | "signoff_requested"
  | "signoff_decision"
  | "deal_ready_for_paralegal"
  | "deal_ready_for_finance"
  | "deal_signed_off"
  | "deal_finance_milestone"
  | "deal_sent_back"
  | "commission_auth_requested"
  | "commission_auth_decision"
  | "deal_cancellation_requested"
  | "deal_cancellation_authorised"
  | "deal_cancellation_rejected"
  | "deal_cancellation_reopened";

export interface AppNotification {
  id: number;
  notification_type: NotificationType;
  title: string;
  body: string;
  /** Frontend-relative path to navigate to on click (may be empty). */
  link: string;
  is_read: boolean;
  read_at: string | null;
  created_at: string;
  /**
   * WF-24: true while the work item this alert raised is still open -- the
   * recipient must act. False for an information-only alert.
   */
  action_required?: boolean;
}

interface Paginated<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

/** List my notifications (optionally only unread). */
export async function listNotifications(
  opts: { unread?: boolean } = {},
  signal?: AbortSignal,
): Promise<AppNotification[]> {
  const qs = new URLSearchParams();
  if (opts.unread) qs.set("unread", "true");
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  const data = await apiRequest<Paginated<AppNotification> | AppNotification[]>(
    `/notifications/${suffix}`,
    { method: "GET" },
    signal,
  );
  return Array.isArray(data) ? data : data.results;
}

/** My unread count -- the bell badge source. */
export async function getUnreadCount(signal?: AbortSignal): Promise<number> {
  const data = await apiRequest<{ unread: number }>(
    "/notifications/unread-count/",
    { method: "GET" },
    signal,
  );
  return data.unread;
}

/** Mark one of my notifications read. */
export async function markNotificationRead(id: number): Promise<AppNotification> {
  return apiRequest<AppNotification>(`/notifications/${id}/mark-read/`, {
    method: "POST",
  });
}

/** My notification preferences (#3821), resolved against my role default. */
export interface NotificationPreferences {
  /** Effective value: do I receive "New deal to process" alerts? */
  new_deal_alerts: boolean;
  /** What my role gets when I have not chosen (off for super-users). */
  new_deal_alerts_default: boolean;
  /** True when I have made my own choice (false = following the default). */
  new_deal_alerts_custom: boolean;
  /** Whether new-deal alerts can reach me at all (deal-processing audience). */
  new_deal_alerts_applicable: boolean;
  /** Do I receive upcoming staff-birthday alerts? Default on (#4041). */
  staff_birthday_alerts?: boolean;
  /** Whether staff-birthday alerts can reach me at all (HR / managers / super-user). */
  staff_birthday_alerts_applicable?: boolean;
}

export async function getNotificationPreferences(
  signal?: AbortSignal,
): Promise<NotificationPreferences> {
  return apiRequest<NotificationPreferences>(
    "/notifications/preferences/",
    { method: "GET" },
    signal,
  );
}

/**
 * Store my choice. ``new_deal_alerts: null`` returns me to my role default;
 * ``staff_birthday_alerts`` switches staff-birthday alerts on/off (#4041).
 */
export async function updateNotificationPreferences(update: {
  new_deal_alerts?: boolean | null;
  staff_birthday_alerts?: boolean;
}): Promise<NotificationPreferences> {
  return apiRequest<NotificationPreferences>("/notifications/preferences/", {
    method: "PATCH",
    body: JSON.stringify(update),
  });
}

/** Mark all my notifications read. Returns the number marked. */
export async function markAllNotificationsRead(): Promise<number> {
  const data = await apiRequest<{ marked_read: number }>(
    "/notifications/mark-all-read/",
    { method: "POST" },
  );
  return data.marked_read;
}
